// Shell Linux léger, en mémoire, pour le simulateur.
//
// Pas de dépendance (les « vraies » libs Linux-in-JS sont des VM WASM lourdes) :
// un système de fichiers virtuel + les commandes de base réellement utiles en
// contexte CKAD/CKS (manipulation de manifestes, inspection). Partage le même
// terminal que le moteur kubectl.

export interface ShellResult {
  text: string;
  isError: boolean;
}

interface VFile {
  type: 'file';
  content: string;
}
interface VDir {
  type: 'dir';
  children: Record<string, VNode>;
}
type VNode = VFile | VDir;

function newDir(): VDir {
  return { type: 'dir', children: {} };
}

export class LinuxShell {
  private root: VDir;
  private cwd: string; // chemin absolu

  constructor() {
    this.root = newDir();
    this.cwd = '/root';
    this.bootstrap();
  }

  reset(): void {
    this.root = newDir();
    this.cwd = '/root';
    this.bootstrap();
  }

  /** Lit un fichier (chemin relatif au cwd ou absolu). null si absent/dossier. */
  readFile(path: string): string | null {
    const node = this.getNode(this.normalize(path));
    return node && node.type === 'file' ? node.content : null;
  }

  /** Écrit un fichier (chemin relatif au cwd ou absolu). Pour la redirection. */
  saveFile(path: string, content: string): boolean {
    return this.writeFile(this.normalize(path), content);
  }

  /** Noms des entrées d'un répertoire (défaut : cwd) — pour la complétion Tab. */
  listNames(dir?: string): string[] {
    const node = this.getNode(this.normalize(dir ?? '.'));
    if (!node || node.type !== 'dir') return [];
    return Object.keys(node.children).map((n) => (node.children[n].type === 'dir' ? `${n}/` : n));
  }

  private bootstrap(): void {
    // Arborescence minimale d'un hôte Linux.
    this.mkdirp('/root');
    this.mkdirp('/tmp');
    this.mkdirp('/etc');
    this.mkdirp('/home');
    this.writeFile('/etc/hostname', 'ckad-cluster\n');
    this.writeFile('/root/notes.txt', 'Bienvenue. Utilise ce shell pour préparer tes manifestes.\n');
  }

  // ----- Utilitaires de chemin -----

  private normalize(path: string): string {
    let base: string[];
    if (path.startsWith('/')) base = [];
    else if (path.startsWith('~')) { base = ['root']; path = path.slice(1); }
    else base = this.cwd.split('/').filter(Boolean);

    for (const part of path.split('/')) {
      if (part === '' || part === '.') continue;
      if (part === '..') base.pop();
      else base.push(part);
    }
    return '/' + base.join('/');
  }

  private getNode(absPath: string): VNode | null {
    if (absPath === '/') return this.root;
    const parts = absPath.split('/').filter(Boolean);
    let node: VNode = this.root;
    for (const part of parts) {
      if (node.type !== 'dir' || !node.children[part]) return null;
      node = node.children[part];
    }
    return node;
  }

  private getParent(absPath: string): { parent: VDir; name: string } | null {
    const parts = absPath.split('/').filter(Boolean);
    if (parts.length === 0) return null;
    const name = parts.pop() as string;
    const parentPath = '/' + parts.join('/');
    const parent = this.getNode(parentPath);
    if (!parent || parent.type !== 'dir') return null;
    return { parent, name };
  }

  private mkdirp(absPath: string): void {
    const parts = absPath.split('/').filter(Boolean);
    let node = this.root;
    for (const part of parts) {
      if (!node.children[part]) node.children[part] = newDir();
      const child = node.children[part];
      if (child.type !== 'dir') return;
      node = child;
    }
  }

  private writeFile(absPath: string, content: string): boolean {
    // Auto-crée l'arborescence parente (ex. /etc/kubernetes/pki/…) pour que le
    // seed de fichiers à un chemin absolu fonctionne comme sur un vrai serveur.
    const parentPath = '/' + absPath.split('/').filter(Boolean).slice(0, -1).join('/');
    if (!this.getNode(parentPath)) this.mkdirp(parentPath);
    const info = this.getParent(absPath);
    if (!info) return false;
    const existing = info.parent.children[info.name];
    if (existing && existing.type === 'dir') return false;
    info.parent.children[info.name] = { type: 'file', content };
    return true;
  }

  // ----- Exécution -----

  exec(line: string): ShellResult {
    // Redirection de sortie : cmd ... > file  /  >> file
    let redirect: { file: string; append: boolean } | null = null;
    const redirMatch = line.match(/\s(>>?)\s*(\S+)\s*$/);
    let cmdLine = line;
    if (redirMatch) {
      redirect = { file: redirMatch[2], append: redirMatch[1] === '>>' };
      cmdLine = line.slice(0, redirMatch.index);
    }

    const tokens = cmdLine.trim().split(/\s+/);
    const cmd = tokens[0];
    const args = tokens.slice(1);

    let res: ShellResult;
    switch (cmd) {
      case 'pwd': res = this.ok(this.cwd); break;
      case 'ls': res = this.cmdLs(args); break;
      case 'cd': res = this.cmdCd(args); break;
      case 'cat': res = this.cmdCat(args); break;
      case 'echo': res = this.ok(this.expandEcho(args)); break;
      case 'mkdir': res = this.cmdMkdir(args); break;
      case 'touch': res = this.cmdTouch(args); break;
      case 'rm': res = this.cmdRm(args); break;
      case 'cp': res = this.cmdCpMv(args, false); break;
      case 'mv': res = this.cmdCpMv(args, true); break;
      case 'grep': res = this.cmdGrep(args); break;
      case 'head': res = this.cmdHeadTail(args, true); break;
      case 'tail': res = this.cmdHeadTail(args, false); break;
      case 'wc': res = this.cmdWc(args); break;
      case 'whoami': res = this.ok('root'); break;
      case 'hostname': res = this.ok('ckad-cluster'); break;
      case 'env': res = this.ok('HOME=/root\nUSER=root\nPWD=' + this.cwd + '\nSHELL=/bin/bash'); break;
      case 'clear': return this.ok('__CLEAR__');
      case '': return this.ok('');
      default:
        return this.err(`${cmd}: command not found`);
    }

    // Applique la redirection si la commande a réussi et produit du texte.
    if (redirect && !res.isError) {
      const abs = this.normalize(redirect.file);
      const prev = redirect.append ? (this.readIfFile(abs) ?? '') : '';
      const content = prev + (res.text ? res.text + '\n' : '');
      if (!this.writeFile(abs, content)) return this.err(`${redirect.file}: cannot write`);
      return this.ok(''); // redirigé : rien à l'écran
    }
    return res;
  }

  private readIfFile(absPath: string): string | null {
    const node = this.getNode(absPath);
    return node && node.type === 'file' ? node.content : null;
  }

  // echo : gère les guillemets simples/doubles basiques
  private expandEcho(args: string[]): string {
    const joined = args.join(' ');
    return joined.replace(/^["']|["']$/g, '');
  }

  private cmdLs(args: string[]): ShellResult {
    const long = args.includes('-l') || args.includes('-la') || args.includes('-al');
    const paths = args.filter((a) => !a.startsWith('-'));
    const target = paths[0] || this.cwd;
    const abs = this.normalize(target);
    const node = this.getNode(abs);
    if (!node) return this.err(`ls: cannot access '${target}': No such file or directory`);
    if (node.type === 'file') return this.ok(target);
    const names = Object.keys(node.children).sort();
    if (names.length === 0) return this.ok('');
    if (!long) return this.ok(names.join('  '));
    const lines = names.map((n) => {
      const child = node.children[n];
      const isDir = child.type === 'dir';
      const size = child.type === 'file' ? child.content.length : 4096;
      return `${isDir ? 'drwxr-xr-x' : '-rw-r--r--'}  root root  ${String(size).padStart(5)}  ${n}${isDir ? '/' : ''}`;
    });
    return this.ok(lines.join('\n'));
  }

  private cmdCd(args: string[]): ShellResult {
    const target = args[0] || '~';
    const abs = this.normalize(target);
    const node = this.getNode(abs);
    if (!node) return this.err(`cd: ${target}: No such file or directory`);
    if (node.type !== 'dir') return this.err(`cd: ${target}: Not a directory`);
    this.cwd = abs;
    return this.ok('');
  }

  private cmdCat(args: string[]): ShellResult {
    if (args.length === 0) return this.err('cat: missing operand');
    const out: string[] = [];
    for (const a of args) {
      const node = this.getNode(this.normalize(a));
      if (!node) return this.err(`cat: ${a}: No such file or directory`);
      if (node.type !== 'file') return this.err(`cat: ${a}: Is a directory`);
      out.push(node.content.replace(/\n$/, ''));
    }
    return this.ok(out.join('\n'));
  }

  private cmdMkdir(args: string[]): ShellResult {
    const recursive = args.includes('-p');
    const dirs = args.filter((a) => !a.startsWith('-'));
    if (dirs.length === 0) return this.err('mkdir: missing operand');
    for (const d of dirs) {
      const abs = this.normalize(d);
      if (this.getNode(abs)) { if (!recursive) return this.err(`mkdir: cannot create directory '${d}': File exists`); continue; }
      if (recursive) { this.mkdirp(abs); continue; }
      const info = this.getParent(abs);
      if (!info || info.parent.type !== 'dir' || !this.getNode(this.normalize(d + '/..'))) {
        return this.err(`mkdir: cannot create directory '${d}': No such file or directory`);
      }
      info.parent.children[info.name] = newDir();
    }
    return this.ok('');
  }

  private cmdTouch(args: string[]): ShellResult {
    if (args.length === 0) return this.err('touch: missing file operand');
    for (const f of args) {
      const abs = this.normalize(f);
      if (this.getNode(abs)) continue;
      if (!this.writeFile(abs, '')) return this.err(`touch: cannot touch '${f}': No such file or directory`);
    }
    return this.ok('');
  }

  private cmdRm(args: string[]): ShellResult {
    const recursive = args.includes('-r') || args.includes('-rf') || args.includes('-fr') || args.includes('-R');
    const targets = args.filter((a) => !a.startsWith('-'));
    if (targets.length === 0) return this.err('rm: missing operand');
    for (const t of targets) {
      const abs = this.normalize(t);
      const node = this.getNode(abs);
      if (!node) return this.err(`rm: cannot remove '${t}': No such file or directory`);
      if (node.type === 'dir' && !recursive) return this.err(`rm: cannot remove '${t}': Is a directory`);
      const info = this.getParent(abs);
      if (!info) return this.err(`rm: cannot remove '${t}'`);
      delete info.parent.children[info.name];
    }
    return this.ok('');
  }

  private cmdCpMv(args: string[], move: boolean): ShellResult {
    const files = args.filter((a) => !a.startsWith('-'));
    const name = move ? 'mv' : 'cp';
    if (files.length < 2) return this.err(`${name}: missing destination file operand`);
    const [src, dst] = files;
    const srcAbs = this.normalize(src);
    const node = this.getNode(srcAbs);
    if (!node) return this.err(`${name}: cannot stat '${src}': No such file or directory`);
    if (node.type !== 'file') return this.err(`${name}: '${src}': directories not supported in this simulator`);
    let dstAbs = this.normalize(dst);
    const dstNode = this.getNode(dstAbs);
    if (dstNode && dstNode.type === 'dir') dstAbs = this.normalize(dst + '/' + src.split('/').pop());
    if (!this.writeFile(dstAbs, node.content)) return this.err(`${name}: cannot create '${dst}'`);
    if (move) {
      const info = this.getParent(srcAbs);
      if (info) delete info.parent.children[info.name];
    }
    return this.ok('');
  }

  private cmdGrep(args: string[]): ShellResult {
    const ci = args.includes('-i');
    const rest = args.filter((a) => !a.startsWith('-'));
    if (rest.length < 2) return this.err('usage: grep [-i] PATTERN FILE');
    const [pattern, file] = rest;
    const node = this.getNode(this.normalize(file));
    if (!node) return this.err(`grep: ${file}: No such file or directory`);
    if (node.type !== 'file') return this.err(`grep: ${file}: Is a directory`);
    const needle = ci ? pattern.toLowerCase() : pattern;
    const matched = node.content.split('\n').filter((l) => (ci ? l.toLowerCase() : l).includes(needle));
    return this.ok(matched.join('\n'));
  }

  private cmdHeadTail(args: string[], head: boolean): ShellResult {
    let n = 10;
    const ni = args.indexOf('-n');
    if (ni >= 0 && args[ni + 1]) n = parseInt(args[ni + 1], 10) || 10;
    const file = args.filter((a) => !a.startsWith('-') && !/^\d+$/.test(a))[0];
    if (!file) return this.err(`${head ? 'head' : 'tail'}: missing file operand`);
    const node = this.getNode(this.normalize(file));
    if (!node) return this.err(`${head ? 'head' : 'tail'}: cannot open '${file}'`);
    if (node.type !== 'file') return this.err(`${head ? 'head' : 'tail'}: ${file}: Is a directory`);
    const lines = node.content.replace(/\n$/, '').split('\n');
    const slice = head ? lines.slice(0, n) : lines.slice(-n);
    return this.ok(slice.join('\n'));
  }

  private cmdWc(args: string[]): ShellResult {
    const linesOnly = args.includes('-l');
    const file = args.filter((a) => !a.startsWith('-'))[0];
    if (!file) return this.err('wc: missing file operand');
    const node = this.getNode(this.normalize(file));
    if (!node) return this.err(`wc: ${file}: No such file or directory`);
    if (node.type !== 'file') return this.err(`wc: ${file}: Is a directory`);
    const content = node.content;
    const nl = content.split('\n').length - (content.endsWith('\n') ? 1 : 0);
    if (linesOnly) return this.ok(`${nl} ${file}`);
    const words = content.split(/\s+/).filter(Boolean).length;
    return this.ok(`${nl} ${words} ${content.length} ${file}`);
  }

  private ok(text: string): ShellResult { return { text, isError: false }; }
  private err(text: string): ShellResult { return { text, isError: true }; }
}
